import React, { useState } from "react";
import { Box, Button, Chip, Divider, Grid, Typography } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";

import palette from "../../../common/styles/palette";
import { EixoDoRelatorio, ApontamentoDoEixo } from "./checklistReport";
import { TimestampChip } from "../debriefing-checklist/timestampLinks";
import { ChecklistIntegro } from "./ChecklistIntegroSection";
import { AiAnalysis, AiChecklistItem } from "../../../services/sheai/SheAiChecklistTypes";
import {
  AiCallout,
  AiCard,
  AiDownloadButton,
  AiPill,
  AiSectionHeader,
} from "./aiSurface";

/**
 * O "Relatório de análise da estação": a leitura curada da análise para o
 * professor — parecer geral, eixos com acertos/pontos a melhorar e a
 * auditoria item a item com a Detecção autorada ao lado da evidência.
 *
 * Regras desta seção, por decisão do cliente:
 * - SEM nota da IA em nenhuma superfície (quem pontua é o professor);
 * - nada é fabricado: campo que a rodada não trouxe é OMITIDO, nunca
 *   preenchido com texto default;
 * - o parecer geral (`gemini_summary`) é derivado pelo agente da síntese
 *   global do grafo ADK — quando ausente, o card não aparece.
 */

/** Rótulos de Veredito, alinhados à aba Checklist. */
const DICA_STATUS: Record<string, { rotulo: string; cor: string; fundo: string; borda: string }> = {
  done: { rotulo: "Fez", cor: "#0a6b46", fundo: palette.emeraldSoft, borda: palette.emeraldStrong },
  partially_done: { rotulo: "Fez parcialmente", cor: "#8a4b00", fundo: palette.warningSoft, borda: palette.warning },
  done_wrong: { rotulo: "Fez incorreto", cor: palette.dangerText, fundo: palette.dangerSoft, borda: palette.dangerText },
  not_done: { rotulo: "Não fez", cor: palette.textMuted, fundo: palette.muted, borda: palette.border },
};

const ColunaApontamentos: React.FC<{
  titulo: string;
  icone: React.ReactNode;
  cor: string;
  fundo: string;
  borda: string;
  apontamentos: ApontamentoDoEixo[];
  vazio: string;
}> = ({ titulo, icone, cor, fundo, borda, apontamentos, vazio }) => (
  <Box
    sx={{
      flex: 1,
      minWidth: 260,
      borderRadius: "8px",
      border: `1px solid ${borda}`,
      backgroundColor: fundo,
      px: 2,
      py: 1.5,
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1 }}>
      {icone}
      <Typography
        sx={{
          fontSize: "0.75rem",
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: "0.04em",
          color: cor,
        }}
      >
        {titulo}
      </Typography>
    </Box>

    {apontamentos.length === 0 ? (
      <Typography variant="body2" sx={{ color: palette.textMuted }}>
        {vazio}
      </Typography>
    ) : (
      <Box component="ul" sx={{ m: 0, pl: 2.5, display: "grid", gap: 0.75 }}>
        {apontamentos.map((a) => (
          <Box
            component="li"
            key={a.itemId}
            sx={{ fontSize: "0.85rem", lineHeight: 1.5, color: palette.text }}
          >
            <Box component="span" sx={{ fontWeight: 600 }}>
              {a.pergunta}
            </Box>
            {a.classe ? (
              <Box component="span" sx={{ color: palette.textMuted }}>
                {" "}
                ({a.classe})
              </Box>
            ) : null}
            {a.critico && (
              <>
                {" "}
                <AiPill label="Crítico" tom="perigo" solido />
              </>
            )}
            {a.observacao ? ` — ${a.observacao}` : ""}
            {a.ts ? (
              <>
                {" "}
                <TimestampChip label={a.ts} ts={a.ts} sourceId={a.sourceId} />
              </>
            ) : null}
          </Box>
        ))}
      </Box>
    )}
  </Box>
);

/** Lista simples de frases do parecer geral (acertos / pontos a melhorar). */
const ColunaTexto: React.FC<{
  titulo: string;
  icone: React.ReactNode;
  cor: string;
  fundo: string;
  borda: string;
  itens: string[];
}> = ({ titulo, icone, cor, fundo, borda, itens }) => (
  <Box
    sx={{
      flex: 1,
      minWidth: 260,
      borderRadius: "8px",
      border: `1px solid ${borda}`,
      backgroundColor: fundo,
      px: 2,
      py: 1.5,
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1 }}>
      {icone}
      <Typography
        sx={{
          fontSize: "0.75rem",
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: "0.04em",
          color: cor,
        }}
      >
        {titulo}
      </Typography>
    </Box>

    <Box component="ul" sx={{ m: 0, pl: 2.5, display: "grid", gap: 0.75 }}>
      {itens.map((itemStr, idx) => (
        <Box
          component="li"
          key={idx}
          sx={{ fontSize: "0.85rem", lineHeight: 1.5, color: palette.text }}
        >
          {itemStr}
        </Box>
      ))}
    </Box>
  </Box>
);

interface ChecklistReportSectionProps {
  eixos: EixoDoRelatorio[];
  analise?: AiAnalysis | null;
  checklist?: ChecklistIntegro | null;
  relatorioDocx?: string;
}

const ChecklistReportSection: React.FC<ChecklistReportSectionProps> = ({
  eixos,
  analise,
  checklist,
  relatorioDocx,
}) => {
  const [detalhesAbertos, setDetalhesAbertos] = useState<Record<string, boolean>>({});
  const [todosAbertos, setTodosAbertos] = useState<boolean>(false);

  if (eixos.length === 0) return null;

  // O parecer geral só existe quando o agente derivou a síntese global da
  // rodada. Ausente => o card inteiro é omitido; três caixas de "não
  // disponível" empilhadas não informam nada.
  const geminiSummary = analise?.gemini_summary;
  const parecerComentario = (geminiSummary?.general_comment || "").trim();
  const parecerAcertos = geminiSummary?.strengths || [];
  const parecerMelhorar = geminiSummary?.points_to_improve || [];
  const temParecerGeral =
    parecerComentario.length > 0 ||
    parecerAcertos.length > 0 ||
    parecerMelhorar.length > 0;

  // Mapa de itens autorados pelo professor na estação (para a Detecção)
  const mapaItensChecklist = new Map<number | string, any>();
  (checklist?.skills || []).forEach((skill) => {
    (skill.items || []).forEach((item) => {
      if (item.item_id) {
        mapaItensChecklist.set(item.item_id, item);
        mapaItensChecklist.set(String(item.item_id), item);
      }
    });
  });

  // Mapa de itens avaliados pela IA (justificativa e veredito)
  const mapaItensIa = new Map<number | string, AiChecklistItem>();
  (analise?.items || []).forEach((item) => {
    mapaItensIa.set(item.item_id, item);
    mapaItensIa.set(String(item.item_id), item);
  });

  const toggleDetalhe = (id: string | number) => {
    setDetalhesAbertos((prev) => {
      const atual = prev[String(id)] ?? todosAbertos;
      return { ...prev, [String(id)]: !atual };
    });
  };

  const toggleTodos = () => {
    setTodosAbertos(!todosAbertos);
    setDetalhesAbertos({});
  };

  return (
    <Box sx={{ mb: 4 }}>
      <AiSectionHeader
        icone={<DescriptionOutlinedIcon sx={{ color: palette.brand, mt: 0.25 }} />}
        titulo="Relatório de análise da estação"
        subtitulo="Consolidação dos objetivos, acertos e pontos de melhoria por eixo avaliado, com justificativa baseada estritamente nas regras do checklist e nas evidências do vídeo."
        acao={
          relatorioDocx && (
            <AiDownloadButton href={relatorioDocx}>Baixar .docx</AiDownloadButton>
          )
        }
      />

      {/* PARECER GERAL DA ANÁLISE — só quando a rodada o trouxe. */}
      {temParecerGeral && (
        <AiCard sx={{ mb: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
            <AutoAwesomeIcon sx={{ color: palette.brand, fontSize: 20 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: palette.brandDark }}>
              Parecer geral da análise
            </Typography>
          </Box>

          {parecerComentario && (
            <Typography variant="body2" sx={{ color: palette.text, mb: 2, lineHeight: 1.6 }}>
              {parecerComentario}
            </Typography>
          )}

          {(parecerAcertos.length > 0 || parecerMelhorar.length > 0) && (
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              {parecerAcertos.length > 0 && (
                <ColunaTexto
                  titulo="Acertos observados"
                  icone={<CheckCircleOutlineIcon sx={{ fontSize: 16, color: palette.emeraldDeep }} />}
                  cor={palette.emeraldDeep}
                  fundo={palette.emeraldSoft}
                  borda={palette.emeraldStrong}
                  itens={parecerAcertos}
                />
              )}
              {parecerMelhorar.length > 0 && (
                <ColunaTexto
                  titulo="Pontos a melhorar"
                  icone={<ReportProblemOutlinedIcon sx={{ fontSize: 16, color: palette.warning }} />}
                  cor={palette.warning}
                  fundo={palette.warningSoft}
                  borda={palette.warning}
                  itens={parecerMelhorar}
                />
              )}
            </Box>
          )}
        </AiCard>
      )}

      {/* EIXOS (Habilidades) AVALIADOS */}
      {eixos.map((eixo) => (
        <AiCard key={eixo.nome} sx={{ mb: 3 }}>
          {/* Cabeçalho do Eixo */}
          <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
            <Typography
              sx={{ fontSize: "0.75rem", fontWeight: 700, color: palette.lilacMid }}
            >
              {String(eixo.ordem).padStart(2, "0")}
            </Typography>
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, color: palette.brandDark, lineHeight: 1.2 }}
            >
              {eixo.nome}
            </Typography>
          </Box>

          {eixo.objetivo && (
            <Typography
              variant="body2"
              sx={{ fontStyle: "italic", color: palette.textMuted, mt: 1 }}
            >
              {eixo.objetivo}
            </Typography>
          )}

          {eixo.criticosFalhados.length > 0 && (
            <AiCallout
              tom="perigo"
              sx={{ mt: 1.75, mb: 0 }}
              icone={
                <ErrorOutlineIcon
                  sx={{ fontSize: 18, color: palette.dangerText, mt: 0.25 }}
                />
              }
              titulo="Item crítico não cumprido (eliminatório declarado pelo professor)"
            >
              {eixo.criticosFalhados.join("; ")}
            </AiCallout>
          )}

          {/* Resumo do desempenho nos Itens do eixo */}
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mt: 2, mb: 3 }}>
            <ColunaApontamentos
              titulo="Acertos"
              icone={
                <CheckCircleOutlineIcon
                  sx={{ fontSize: 16, color: palette.emeraldDeep }}
                />
              }
              cor={palette.emeraldDeep}
              fundo={palette.emeraldSoft}
              borda={palette.emeraldStrong}
              apontamentos={eixo.acertos}
              vazio="Nenhum item deste eixo foi classificado como feito corretamente."
            />
            <ColunaApontamentos
              titulo="Pontos a melhorar"
              icone={
                <ReportProblemOutlinedIcon
                  sx={{ fontSize: 16, color: palette.warning }}
                />
              }
              cor={palette.warning}
              fundo={palette.warningSoft}
              borda={palette.warning}
              apontamentos={eixo.melhorar}
              vazio="Sem ressalvas neste eixo."
            />
          </Box>

          <Divider sx={{ my: 2.5 }} />

          {/* AUDITORIA ITEM A ITEM: a Detecção autorada ao lado da evidência */}
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2, flexWrap: "wrap", gap: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <AssignmentTurnedInIcon sx={{ color: palette.brand, fontSize: 20 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: palette.brandDark, textTransform: "uppercase", letterSpacing: "0.03em" }}>
                  Auditoria de conformidade item a item
                </Typography>
              </Box>
              <Button
                size="small"
                variant="text"
                onClick={toggleTodos}
                sx={{ textTransform: "none", fontSize: "0.75rem", fontWeight: 700, color: palette.brand }}
              >
                {todosAbertos ? "Recolher todos" : "Expandir todos"}
              </Button>
            </Box>

            {/* Listagem dos itens */}
            {[...eixo.acertos, ...eixo.melhorar].map((apontamento) => {
              const itemChecklist = mapaItensChecklist.get(apontamento.itemId);
              const itemIa = mapaItensIa.get(apontamento.itemId);
              const statusCfg = DICA_STATUS[itemIa?.result || (apontamento.classe ? "partially_done" : "done")] || DICA_STATUS.done;
              const detection = itemChecklist?.detection || {};
              const temDet = Object.values(detection).some((v: any) => v && String(v).trim() !== "");
              const estaAberto = detalhesAbertos[String(apontamento.itemId)] ?? todosAbertos;
              const evidencia = itemIa?.justification || apontamento.observacao;
              const trechos = itemIa?.evidence || [];

              return (
                <Box
                  key={apontamento.itemId}
                  sx={{
                    mb: 2,
                    border: `1px solid ${palette.border}`,
                    borderRadius: "8px",
                    overflow: "hidden",
                    backgroundColor: palette.surface,
                  }}
                >
                  {/* Header do Item */}
                  <Box
                    onClick={() => toggleDetalhe(apontamento.itemId)}
                    sx={{
                      p: 1.5,
                      backgroundColor: palette.brandWash,
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      cursor: "pointer",
                      gap: 1.5,
                      flexWrap: "wrap",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flex: 1 }}>
                      <Chip
                        label={statusCfg.rotulo}
                        size="small"
                        sx={{
                          backgroundColor: statusCfg.fundo,
                          color: statusCfg.cor,
                          fontWeight: 700,
                          fontSize: "0.75rem",
                          border: `1px solid ${statusCfg.borda}`,
                        }}
                      />
                      <Typography sx={{ fontWeight: 700, color: palette.text, fontSize: "0.9rem" }}>
                        {apontamento.pergunta}
                      </Typography>
                      {apontamento.critico && <AiPill label="Crítico" tom="perigo" solido />}
                    </Box>
                    <ExpandMoreIcon
                      sx={{
                        transform: estaAberto ? "rotate(180deg)" : "rotate(0deg)",
                        transition: "0.2s",
                        color: palette.textMuted,
                      }}
                    />
                  </Box>

                  {estaAberto && (
                    <Box sx={{ p: 2 }}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <Box sx={{ p: 1.5, height: "100%", backgroundColor: palette.background, borderRadius: "6px", border: `1px solid ${palette.border}` }}>
                            <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: palette.brandDark, textTransform: "uppercase", mb: 1 }}>
                              Detecção do Item (regra do professor)
                            </Typography>
                            {temDet ? (
                              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, fontSize: "0.8rem" }}>
                                {detection.video && <Typography variant="caption"><strong>O que ver:</strong> {detection.video}</Typography>}
                                {detection.speech && <Typography variant="caption"><strong>O que ouvir:</strong> {detection.speech}</Typography>}
                                {detection.correct_when && <Typography variant="caption" sx={{ color: "#0a6b46", fontWeight: 600 }}><strong>Correto quando:</strong> {detection.correct_when}</Typography>}
                                {detection.not_done_when && <Typography variant="caption" sx={{ color: palette.dangerText }}><strong>Não realizou quando:</strong> {detection.not_done_when}</Typography>}
                                {detection.do_not_penalize && <Typography variant="caption" sx={{ color: palette.textMuted }}><strong>Não penalizar por:</strong> {detection.do_not_penalize}</Typography>}
                              </Box>
                            ) : (
                              <Typography variant="caption" sx={{ color: palette.textMuted }}>
                                Sem regra de Detecção autorada para este Item.
                              </Typography>
                            )}
                          </Box>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Box sx={{ p: 1.5, height: "100%", backgroundColor: palette.background, borderRadius: "6px", border: `1px solid ${palette.border}` }}>
                            <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: palette.brandDark, textTransform: "uppercase", mb: 1 }}>
                              Evidência no vídeo
                            </Typography>
                            {evidencia ? (
                              <Typography variant="body2" sx={{ fontSize: "0.85rem", color: palette.text, mb: 1 }}>
                                {evidencia}
                              </Typography>
                            ) : (
                              <Typography variant="caption" sx={{ color: palette.textMuted }}>
                                A rodada não registrou justificativa para este Item.
                              </Typography>
                            )}
                            {trechos.length > 0 ? (
                              <Box component="ul" sx={{ m: 0, pl: 2, display: "grid", gap: 1 }}>
                                {trechos.map((trecho, index) => (
                                  <Box component="li" key={`${trecho.source_id}-${trecho.ts_start}-${index}`}>
                                    <TimestampChip
                                      label={trecho.ts_end && trecho.ts_end !== trecho.ts_start
                                        ? `${trecho.ts_start}-${trecho.ts_end}` : trecho.ts_start}
                                      ts={trecho.ts_start} sourceId={trecho.source_id}
                                    />{" "}
                                    <Typography component="span" variant="body2">{trecho.description}</Typography>
                                  </Box>
                                ))}
                              </Box>
                            ) : apontamento.ts && (
                              <TimestampChip label={`Momento: ${apontamento.ts}`} ts={apontamento.ts} sourceId={apontamento.sourceId} />
                            )}
                          </Box>
                        </Grid>
                      </Grid>
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>

          {/* Próximo passo pedagógico — só quando o agente o envia. */}
          {eixo.proximoPasso && (
            <Box
              sx={{
                mt: 2,
                px: 2,
                py: 1.25,
                borderRadius: "8px",
                backgroundColor: palette.brandWash,
                display: "flex",
                alignItems: "flex-start",
                gap: 1,
              }}
            >
              <ArrowForwardIcon sx={{ fontSize: 16, color: palette.brand, mt: 0.25 }} />
              <Typography variant="body2" sx={{ color: palette.text }}>
                <Box component="span" sx={{ fontWeight: 700 }}>
                  Próximo passo:
                </Box>{" "}
                {eixo.proximoPasso}
              </Typography>
            </Box>
          )}
        </AiCard>
      ))}
    </Box>
  );
};

export default ChecklistReportSection;
